import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestMode, setGuestMode] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const isGuest = localStorage.getItem('guestMode') === 'true';
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (isGuest) {
      setGuestMode(true);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.token);
    localStorage.removeItem('guestMode');
    setGuestMode(false);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    await fetchUser();
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem('token', response.data.token);
    localStorage.removeItem('guestMode');
    setGuestMode(false);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    await fetchUser();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('guestMode');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setGuestMode(false);
  };

  const enterGuestMode = () => {
    localStorage.setItem('guestMode', 'true');
    setGuestMode(true);
  };

  const exitGuestMode = () => {
    localStorage.removeItem('guestMode');
    setGuestMode(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      refreshUser: fetchUser,
      guestMode,
      enterGuestMode,
      exitGuestMode
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};